import { autoinject, computedFrom, TaskQueue } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { Location } from "../../models/location";
import { dispatchify, Store } from 'aurelia-store';
import { getZoneMeasurements } from "../../store/actions/measurement-actions";
import { MdModal, MdTabs, MdCollapsible } from "aurelia-materialize-bridge";
import * as State from "../../store/state";
import IState = State.IState;
import { Subscription } from "rxjs";
import { Api } from "../../services/api";
import { DigitalOutputToggleRequestViewModel } from "../../models/digital-output-toggle-request-view-model";
import * as Linq from "linq-es2015";
import { CurtainOperation } from "./types/curtain-operation";
import { CurtainOperationType } from "./types/curtain-operation-type";
import { CurtainDirection } from "./types/curtain-direction";

@autoinject
export class LocationTableau {
	locations: Array<Location>;
	selectedLocation: Location;
	isActive: boolean = false;
	disabled: boolean = false;
	delay: number = 35;
	preventCollapse: boolean;
	tabs: MdTabs;
	api: Api;
	locationsHaveCurtains: boolean;
	curtainModal: MdModal;
	curtainMessage: string;
	curtainOperation: CurtainOperation;

	tabsContainer: HTMLElement;


	protected applicationState: IState;
	protected subscription: Subscription;
	protected store: Store<IState>;
	protected eventAggregator: EventAggregator;

	constructor(store: Store<IState>, eventAggregator: EventAggregator, api: Api, private taskQueue: TaskQueue) {
		this.store = store;
		this.eventAggregator = eventAggregator;
		this.api = api;
	}

	async bind(bindingContext: any, overrideContext: any) {

		this.subscription = this.store.state.subscribe(async (state) => {
			this.applicationState = state;
			if (this.applicationState.locationState.initialized) {
				this.locations = this.applicationState.locationState.selectedLocations;
				this.locationsHaveCurtains = this.determineLocationsHaveCurtains();
				if (!this.selectedLocation && this.locations && this.locations.length > 0) {
					this.selectedLocation = this.getCurrentLocation();

					const firstZone = this.selectedLocation.zones[0];
					if (firstZone) {
						await this.getZoneData(this.selectedLocation.id, firstZone.id);
					}
					this.tabs.refresh();
				}
			}
		});
	}

	attached(args: any) {

		
		// wait 100 ms to allow the MdTabs attached method to fire and thus create an instance of the underlying tab control
		// otherwise the call to tabs.select will fail.
		setTimeout(() => {



			if (this.selectedLocation) {
				if (this.locations && this.locations.length > 0 &&
					this.selectedLocation &&
					this.selectedLocation.id !== this.locations[0].id) {
					this.tabs.select(this.selectedLocation.id);
				}
			}
			else
			{
				this.selectedLocation = this.getCurrentLocation();
				this.tabs.select(this.selectedLocation.id);
			}
			
		}, 100);

		//this.tabsContainer = document.querySelector(".tabs-container") as HTMLElement;

	}

	scrollLeft() {
		this.tabsContainer.scrollBy({ left: -100, behavior: "smooth" });
	}

	scrollRight() {
		this.tabsContainer.scrollBy({ left: 100, behavior: "smooth" });
	}



	getCurrentLocation(): Location {
		const currentLocationId = localStorage.getItem("current-location");
		if (!currentLocationId) {
			return this.locations[0];
		}

		const currentLocations = this.locations.filter(l => l.id === currentLocationId);
		return currentLocations[0];
	}

	determineLocationsHaveCurtains(): boolean {
		const locations = Linq.AsEnumerable(this.locations);
		return locations.Any(location => location.hasCurtainControllers);
	}

	unbind() {
		this.subscription.unsubscribe();
	}

	
	async collapsibleOpen(event) {
		const element = event.detail.el;

		if (this.preventCollapse) {
			return;
		}

		if (element === undefined) {
			event.stopPropagation();
		}

		if (element !== undefined && element.id !== undefined) {
			const ids = element.id.split(":");
			await this.getZoneData(ids[0], ids[1]);
			this.eventAggregator.publish("ZoneOpened");
		}
	}

	async getZoneData(locationId: string, zoneId: string) {
		this.isActive = true;
		await dispatchify(getZoneMeasurements)(locationId, zoneId);
		this.isActive = false;
	}

	async toggleZoneCurtains(zoneId: string, direction: string) {
		this.preventCollapse = true;

		const viewModel = new DigitalOutputToggleRequestViewModel();
		viewModel.deviceIds.push(zoneId);
		viewModel.message = direction;
		const response = await this.api.toggleZoneCurtains(viewModel);
		if (response.success) {
			this.disabled = true;
			setTimeout(() => {
				this.disabled = false;
				this.preventCollapse = false;
				},
				this.delay * 1000);
		}
	}

	async raiseZoneCurtains(zoneId: string) {

		const currentZone = this.selectedLocation.zones.find(zone => zone.id === zoneId);
		this.curtainMessage = `raise the curtains for '${currentZone.name}'`;
		this.curtainOperation = { "type": CurtainOperationType.Zone, "direction": CurtainDirection.up, "target": zoneId };
		this.curtainModal.open();
	}

	async lowerZoneCurtains(zoneId: string) {
		const currentZone = this.selectedLocation.zones.find(zone => zone.id === zoneId);
		this.curtainMessage = `lower the curtains for '${currentZone.name}'`;
		this.curtainOperation = { "type": CurtainOperationType.Zone, "direction": CurtainDirection.down, "target": zoneId };
		this.curtainModal.open();
	}

	async toggleCurtains() {
		switch(this.curtainOperation.type) {
			case CurtainOperationType.Zone:
				await this.toggleZoneCurtains(this.curtainOperation.target, this.curtainOperation.direction);
				break;
			case CurtainOperationType.Location:
				await this.toggleLocationCurtains(this.curtainOperation.target, this.curtainOperation.direction);
				break;
			case CurtainOperationType.Device:
				// no-op
				break;
		}
	}


	async toggleLocationCurtains(locationId: string, direction: string) {
		this.preventCollapse = true;

		const viewModel = new DigitalOutputToggleRequestViewModel();
		viewModel.deviceIds.push(locationId);
		viewModel.message = direction;
		const response = await this.api.toggleLocationCurtains(viewModel);
		if (response.success) {
			this.disabled = true;
			setTimeout(() => {
					this.disabled = false;
					this.preventCollapse = false;
				},
				this.delay * 1000);
		}
	}

	async raiseLocationCurtains(locationId: string) {
		this.curtainMessage = `raise the curtains for all of the zones in ${this.selectedLocation.name}`;
		this.curtainOperation = { "type": CurtainOperationType.Location, "direction": CurtainDirection.up, "target": locationId };
		this.curtainModal.open();
	}

	async lowerLocationCurtains(locationId: string) {
		this.curtainMessage = `lower the curtains for all of the zones in ${this.selectedLocation.name}`;
		this.curtainOperation = { "type": CurtainOperationType.Location, "direction": CurtainDirection.down, "target": locationId };
		this.curtainModal.open();
	}

	async onTabSelected($event) {
		const locationId = $event.detail.substring(1);
		const locationList = Linq.AsEnumerable(this.locations);
		this.selectedLocation = locationList.First(location => location.id === locationId);
		localStorage.setItem("current-location", locationId);
	}

	onConfirmCurtainOperationDialog($event) {

	}
}